import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Col, Row, Card, Modal, Dropdown, Button, Space, Menu, Popconfirm } from 'antd';
import { MdOutlineUnpublished } from 'react-icons/md';
import { useIntl } from 'react-intl';
import { RiArrowLeftLine, RiCloseFill } from 'react-icons/ri';
import { DownOutlined } from '@ant-design/icons';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import dayjs from 'dayjs';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageBonuses from '../../../../../urls/urlPageBonuses';
import urlPageBonusEdit from '../../../../../urls/urlPageBonusEdit';
import { BONUSES_SOURCE } from '../PageBonuses/PageBonuses.const';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../../const/system';
import { Permissions } from '../../../../../const/permissions';
import { HasRights, PrivateComponent } from '../../../../../components/HasRights/HasRights';
import Spinner from '../../../../../components/Spinner';
import getConfigurations from '../../../../../lib/getConfigurations';
import useToggle from '../../../../../app/hooks/useToggle';
import handleResponse from '../../../../../lib/handleResponse';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../layout/components/action-button';
import ContentCard from '../../../components/ContentCard';
import BonusDetailsCard from '../../../components/BonusDetailsCard';
import ImageModalCardBodyView from '../../../components/ImageModalCard/ImageModalCardBodyView';
import EmptyDataPlaceholder from '../../../../../components/EmptyDataPlaceholder';
import { useDeleteBonusMutation, useGetSpecificBonusQuery, useUnpublishBonusMutation } from '../../../api/bonusesApiSlice';



const PageBonusDetails = () => {
  const intl = useIntl();
  const { bonusId } = useParams();
  const navigate = useNavigate();
  const [ previewModalOpen, togglePreviewModal ] = useToggle();
  const [ previewImage, setPreviewImage ] = useState({});

  const { data: bonusDetails = {}, isLoading } = useGetSpecificBonusQuery(bonusId);
  const [ unpublishBonus, { isLoading: isUnpublishBonusLoading } ] = useUnpublishBonusMutation();
  const [ deleteBonus, { isLoading: isBonusDeleting } ] = useDeleteBonusMutation();

  const images = bonusDetails?.images?.data ?? [];
  const bonusConfig = getConfigurations(BONUSES_SOURCE);
  const allowUnpublishPast = bonusConfig?.publish?.allow_unpublish_past ?? true;
  const allowUpdatePast = bonusConfig?.publish?.allow_update_past ?? true;
  const releaseDate = bonusDetails?.released_at;
  const isPublished = Boolean(bonusDetails?.is_published);
  const allowUnpublish = allowUnpublishPast ? isPublished :
    isPublished && dayjs().isSameOrBefore(dayjs(releaseDate), 'day');
  const allowUpdate = allowUpdatePast ? true : dayjs().isSameOrBefore(dayjs(releaseDate).subtract(-1, 'days'), 'day');

  const isLoadingData = isLoading || isUnpublishBonusLoading || isBonusDeleting;


  useEffect(() => {
    const { hash } = window.location;

    if (hash) {
      const element = document.querySelector(hash);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);


  const handleUnpublish = () => {
    unpublishBonus(bonusId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const handleDelete = () => {
    deleteBonus(bonusId)
      .unwrap()
      .then(() => {
        handleResponse(
          'success',
          intl,
          'ui-general-deleted-successfully',
          [ () => navigate(urlPageBonuses()) ],
        );
      })
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleCardClick = (item) => {
    setPreviewImage(item);
    togglePreviewModal();
  };

  const handlePreviewModalClose = () => {
    togglePreviewModal();
  };


  const allowRenderActions = (allowUpdate && HasRights([ Permissions.CONTENT.BONUSES.EDIT ])) ||
    (allowUnpublish && HasRights([ Permissions.CONTENT.BONUSES.UNPUBLISH ])) ||
    (!isPublished && HasRights([ Permissions.CONTENT.BONUSES.DELETE ]));

  const actionItems = (
    <Menu>
      {allowUpdate && (
        <PrivateComponent allowedPermissions={[ Permissions.CONTENT.BONUSES.EDIT ]}>
          <Menu.Item
            key="edit"
            icon={<PiPencilSimpleLine size={18} />}
            onClick={() => navigate(urlPageBonusEdit({ bonusId }))}
          >
            <IntlMessages id="ui-general-edit" />
          </Menu.Item>
        </PrivateComponent>
      )}
      {allowUnpublish && (
        <PrivateComponent allowedPermissions={[ Permissions.CONTENT.BONUSES.UNPUBLISH ]}>
          <Menu.Item
            key="unpublish"
            icon={<MdOutlineUnpublished size={18} />}
            onClick={handleUnpublish}
          >
            <IntlMessages id="ui-general-unpublish" />
          </Menu.Item>
        </PrivateComponent>
      )}
      {!isPublished && (
        <PrivateComponent allowedPermissions={[ Permissions.CONTENT.BONUSES.DELETE ]}>
          <Popconfirm
            title={<IntlMessages id="ui-general-delete-confirm-message" />}
            placement="topLeft"
            okButtonProps={{ danger: true }}
            onConfirm={handleDelete}
          >
            <Menu.Item
              key="delete"
              icon={<PiTrashSimple size={18} />}
              onClick={() => {}}
            >
              <IntlMessages id="ui-general-delete" />
            </Menu.Item>
          </Popconfirm>
        </PrivateComponent>
      )}
    </Menu>
  );

  return (
    <>
      <Modal
        width={1200}
        title={intl.formatMessage({ id: 'content-image-preview-card-title' })}
        centered
        visible={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={false}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <ImageModalCardBodyView data={previewImage} />
      </Modal>

      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageBonuses()}>
              <IntlMessages id='bonuses-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={bonusDetails?.id ?? EMPTY_VALUE_PLACEHOLDER}
        />

        <Col md={10} span={24} className='text-end'>
          <Space size='middle'>
            {allowRenderActions && (
              <Dropdown overlay={actionItems}>
                <Button onClick={(ev) => ev.preventDefault()}>
                  <Space size='middle'>
                    <IntlMessages id='ui-general-action' />
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            )}

            <ActionButton
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() => navigate(urlPageBonuses())}
              title={<IntlMessages id='ui-general-back-to-list' />}
            />
          </Space>
        </Col>
      </Row>

      <Spinner spinning={isLoadingData}>
        <Row gutter={[ 32, 32 ]} className="hp-mb-32">
          <Col span={24}>
            <BonusDetailsCard data={bonusDetails} />
          </Col>
        </Row>

        <Row gutter={[ 32, 32 ]}>
          <Col span={24}>
            <Card
              id="attached-images"
              loading={isLoadingData}
              title={<IntlMessages id="achievements-attached-images" />}
            >
              <div className={images.length > 0 ? 'content-card-grid' : ''}>
                {images.length > 0 ?
                  images.map((item) => {
                    return (
                      <ContentCard
                        key={item.id}
                        data={item}
                        onClick={handleCardClick}
                      />
                    );
                  }) : <EmptyDataPlaceholder placeholder={<IntlMessages id="bonus-no-attached-images" />} />}
              </div>
            </Card>
          </Col>
        </Row>
      </Spinner>
    </>
  );
};

export default PageBonusDetails;
